import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LifeBuoy, Loader2, MessageSquare, ChevronDown } from "lucide-react";
import api, { fmtDate, formatApiError } from "./api";
import { PageHeader, EmptyState, inputCls } from "./components";
import { EASE } from "@/components/Motion";

const TICKET_STYLES = {
  open: "border-amber-400/30 bg-amber-400/10 text-amber-300",
  answered: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  closed: "border-white/15 bg-white/5 text-neutral-400",
};

export default function TicketsPage() {
  const [tickets, setTickets] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/tickets").then(({ data }) => setTickets(data)).catch(() => setTickets([]));
  const [supportEmail, setSupportEmail] = useState("");
  useEffect(() => {
    load();
    api.get("/content").then(({ data }) => setSupportEmail(data.support_email || "")).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/tickets", { subject, message });
      toast.success("Ticket created", { description: "Support usually replies within minutes." });
      setSubject("");
      setMessage("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="tickets-page">
      <PageHeader title="Support Tickets" sub="Real humans, real answers — no bots hiding behind macros." />
      {supportEmail && (
        <p className="-mt-4 mb-6 text-sm text-neutral-500">
          Prefer email? Write to{" "}
          <a href={`mailto:${supportEmail}`} data-testid="support-email-link"
            className="text-gold underline underline-offset-4 transition-colors hover:text-goldlight">
            {supportEmail}
          </a>
        </p>
      )}

      <div className="grid gap-6 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8 lg:col-span-5"
        >
          <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
            <MessageSquare size={16} className="text-gold" /> New ticket
          </h3>
          <form onSubmit={create} className="mt-6 space-y-4" data-testid="ticket-form">
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              required
              minLength={4}
              className={inputCls}
              data-testid="ticket-subject-input"
            />
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe the issue — order IDs help us move faster."
              required
              minLength={10}
              rows={5}
              className={`${inputCls} resize-none`}
              data-testid="ticket-message-input"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="ticket-submit-button"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold tracking-[0.12em] text-black transition-shadow duration-300 hover:shadow-[0_8px_30px_rgb(var(--gold)_/_0.3)] disabled:opacity-50"
            >
              {loading && <Loader2 size={15} className="animate-spin" />}
              SUBMIT TICKET
            </button>
          </form>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.12 }}
          className="lg:col-span-7"
        >
          {tickets === null ? (
            <div className="flex justify-center py-24">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/20 border-t-gold" />
            </div>
          ) : tickets.length === 0 ? (
            <EmptyState icon={LifeBuoy} title="No tickets yet" sub="Everything running smoothly, then." testid="tickets-empty" />
          ) : (
            <div className="space-y-3" data-testid="tickets-list">
              {tickets.map((t) => {
                const open = openId === t.id;
                return (
                  <div key={t.id} className="overflow-hidden rounded-2xl border border-white/10 bg-panel/80" data-testid={`ticket-${t.ticket_id}`}>
                    <button
                      onClick={() => setOpenId(open ? null : t.id)}
                      data-testid={`ticket-toggle-${t.ticket_id}`}
                      className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left transition-colors hover:bg-white/[0.02]"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-xs text-gold/80">{t.ticket_id}</span>
                          <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${TICKET_STYLES[t.status] || TICKET_STYLES.open}`}>
                            {t.status}
                          </span>
                        </div>
                        <div className="mt-1.5 truncate text-sm font-medium text-neutral-100">{t.subject}</div>
                      </div>
                      <ChevronDown size={16} className={`shrink-0 text-neutral-500 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                    </button>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        transition={{ duration: 0.4, ease: EASE }}
                        className="border-t border-white/5 px-6 py-5"
                      >
                        <p className="text-sm leading-relaxed text-neutral-300">{t.message}</p>
                        <p className="mt-2 font-mono text-[10px] tracking-[0.15em] text-neutral-600">{fmtDate(t.created_at)}</p>
                        {t.replies.map((r, i) => (
                          <div key={i} className="mt-4 rounded-xl border border-gold/15 bg-gold/[0.05] p-4">
                            <div className="font-mono text-[9px] tracking-[0.25em] text-gold/80">SUPPORT · {fmtDate(r.at)}</div>
                            <p className="mt-2 text-sm leading-relaxed text-neutral-300">{r.body}</p>
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
